import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, googleProvider, ADMIN_EMAILS } from "@/firebase/config";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const ensureUserDoc = async (fbUser) => {
    const ref = doc(db, "users", fbUser.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      const initial = {
        uid: fbUser.uid,
        name: fbUser.displayName || fbUser.email?.split("@")[0] || "Friend",
        email: fbUser.email,
        photoURL: fbUser.photoURL || null,
        plan: "basic",
        bookAccess: false,
        pageUploadLimit: 10,
        status: "active",
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      };
      await setDoc(ref, initial);
      return initial;
    }
    await setDoc(ref, { lastLogin: serverTimestamp() }, { merge: true });
    return snap.data();
  };

  const ensureAdminDoc = async (fbUser) => {
    if (!fbUser?.email) return false;
    const email = fbUser.email.toLowerCase();
    const isBootstrapAdmin = ADMIN_EMAILS.includes(email);
    if (!isBootstrapAdmin) return false;

    try {
      const ref = doc(db, "adminUsers", fbUser.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          uid: fbUser.uid,
          email,
          role: "admin",
          permissions: ["submissions"],
          createdAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.warn("Could not sync admin profile yet:", error);
    }
    return true;
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const adminOk = ADMIN_EMAILS.includes((fbUser.email || "").toLowerCase());
        setUser(fbUser);
        setIsAdmin(adminOk);
        try {
          const data = await ensureUserDoc(fbUser);
          setUserDoc(data);
          await ensureAdminDoc(fbUser);
        } catch (e) {
          console.error("Auth bootstrap error:", e);
        }
      } else {
        setUser(null);
        setUserDoc(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = {
    user,
    userDoc,
    isAdmin,
    loading,
    loginEmail: (email, password) => signInWithEmailAndPassword(auth, email, password),
    loginGoogle: () => signInWithPopup(auth, googleProvider),
    logout: () => signOut(auth),
    refreshUserDoc: async () => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setUserDoc(snap.data());
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
